import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowUpRight,
  Check,
  AlertCircle,
  Loader2,
  Mail,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function Community() {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [emailError, setEmailError] = useState("");
  // status: "idle" | "sending" | "sent" | "error"
  const [status, setStatus] = useState("idle");

  const onChange = (e) => {
    const v = e.target.value;
    setEmail(v);
    if (!v) setEmailError("");
    else if (!EMAIL_REGEX.test(v.trim()))
      setEmailError("Please enter a valid email like name@example.com");
    else setEmailError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (website) return; // honeypot tripped — silent drop
    const v = email.trim();
    if (!EMAIL_REGEX.test(v)) {
      setEmailError("Please enter a valid email like name@example.com");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/subscribers`, { email: v });
      setStatus("sent");
      setEmail("");
      setEmailError("");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "We couldn't add you right now. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Subscription failed.");
      setStatus("error");
      setTimeout(() => setStatus("idle"), 2500);
    }
  };

  return (
    <section
      id="community"
      data-testid="community-section"
      className="py-20 md:py-28 px-6 md:px-12 bg-[#070707] relative overflow-hidden"
    >
      {/* Soft ambient gold glow */}
      <div
        aria-hidden="true"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(212,175,55,0.05) 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        {/* Heading */}
        <div className="text-center mb-14 reveal">
          <div className="inline-flex items-center justify-center gap-3 mb-5">
            <span className="w-8 h-px bg-gold" />
            <span className="text-xs uppercase tracking-overline text-gold">
              Digital Basecamp
            </span>
            <span className="w-8 h-px bg-gold" />
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-3xl mx-auto">
            Travel with us in{" "}
            <em className="italic text-gold">real time</em>.
          </h2>
          <p className="mt-6 text-zinc-400 font-light leading-relaxed max-w-xl mx-auto">
            Join the inner circle. One thoughtful email when the road opens,
            milestones drop, and behind-the-scenes moments arrive — straight to
            your inbox.
          </p>
        </div>

        {/* Minimalist email signup */}
        <form
          onSubmit={submit}
          noValidate
          data-testid="community-email-form"
          className="max-w-xl mx-auto reveal"
        >
          {/* Honeypot */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            <label htmlFor="community-website-hp">Website (leave blank)</label>
            <input
              id="community-website-hp"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          {status === "sent" ? (
            <div
              data-testid="community-success"
              className="border border-gold/30 bg-[#0e0d09] p-6 md:p-7 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-2 duration-500"
            >
              <div className="w-11 h-11 rounded-full bg-gold flex items-center justify-center shrink-0">
                <Check className="w-5 h-5 text-black" strokeWidth={3} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-overline text-gold mb-1">
                  You’re in
                </div>
                <p className="text-sm text-zinc-300 font-light leading-relaxed">
                  We’ll be in touch when the road opens. Welcome to the basecamp.
                </p>
              </div>
              <Sparkles
                className="w-5 h-5 text-gold shrink-0 hidden sm:block"
                strokeWidth={1.5}
              />
            </div>
          ) : (
            <>
              <Label
                htmlFor="community-email"
                className="text-xs uppercase tracking-overline text-zinc-400 mb-3 block text-center"
              >
                Get updates by email
              </Label>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Mail
                    className="w-4 h-4 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2"
                    strokeWidth={1.5}
                  />
                  <Input
                    id="community-email"
                    type="email"
                    data-testid="community-email-input"
                    value={email}
                    onChange={onChange}
                    onBlur={() => {
                      if (email && !EMAIL_REGEX.test(email.trim()))
                        setEmailError(
                          "Please enter a valid email like name@example.com"
                        );
                    }}
                    aria-invalid={!!emailError}
                    aria-describedby={
                      emailError ? "community-email-error" : undefined
                    }
                    placeholder="you@example.com"
                    className={`bg-[#0a0a0a] border rounded-none h-14 pl-12 pr-4 focus-visible:ring-0 text-white placeholder:text-zinc-600 transition-colors ${
                      emailError
                        ? "border-red-500/70 focus-visible:border-red-500"
                        : "border-white/15 focus-visible:border-gold"
                    }`}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={
                    status === "sending" ||
                    !email ||
                    !!emailError ||
                    !EMAIL_REGEX.test(email.trim())
                  }
                  data-testid="community-email-submit"
                  className="h-14 px-7 bg-gold text-black hover:bg-white rounded-none uppercase tracking-overline text-xs disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300"
                >
                  {status === "sending" ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      Subscribe
                      <ArrowUpRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </div>
              {emailError && (
                <div
                  id="community-email-error"
                  data-testid="community-email-error"
                  className="flex items-center gap-2 text-xs text-red-400 mt-3 justify-center"
                >
                  <AlertCircle
                    className="w-3.5 h-3.5 shrink-0"
                    strokeWidth={1.5}
                  />
                  <span>{emailError}</span>
                </div>
              )}
              <p className="mt-4 text-center text-[11px] text-zinc-600">
                One thoughtful message at a time. No spam. Unsubscribe anytime.
              </p>
            </>
          )}
        </form>
      </div>
    </section>
  );
}
